import { getNavLink } from '@/layout/NavLinks';
import { px } from '@/layout/styles/classes';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useLocationData from '@s/hooks/useLocationData';
import CLink from './CLink';
import { theme } from './layout/styles/GlobalStyles';
import { h2 } from './layout/styles/Typography';

const Hero = styled.section`
    text-align: center;

    > div:last-of-type {
        ${px};
        background-image: ${({ theme }) =>
            `linear-gradient(${theme.colors.pink3} 50%, transparent 50%)`};
    }
`;

const TextBox = styled.div`
    ${px};
    padding-top: 32px;
    padding-bottom: 48px;
    background-color: ${({ theme }) => theme.colors.pink3};

    > h1 {
        ${h2};
        margin: 16px auto 0;
        color: ${({ theme }) => theme.colors.gray3};
        max-width: 1064px;
    }

    @media (min-width: 1024px) {
        padding-top: 24px;
        padding-bottom: 80px;

        > h1 {
            margin-top: 32px;
        }
    }
`;

const commonStyles = css`
    text-transform: capitalize;
    white-space: nowrap;
    color: ${theme.colors.green2};
    font-size: 1rem;

    & > span {
        font-weight: 400;
        margin-left: 16px;
        font-size: 1rem;
        color: ${theme.colors.green2};
    }
`;

const commonLinkStyles = css`
    ${commonStyles};
    margin-right: 16px;
    transition: color 0.3s ease-in-out;

    :hover {
        color: ${theme.colors.green1};
    }

    :focus-visible {
        color: ${theme.colors.green1};
    }
`;

type SubProp = { $subCategory: string };

const BreadCrumb = styled.p`
    margin: 0;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;

    > span {
        ${commonStyles};
        font-weight: 700;

        :last-of-type {
            display: inline;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }
`;

const Home = styled(CLink, {
    shouldForwardProp: (propName: string) => !propName.startsWith('$'),
})<SubProp>`
    ${commonLinkStyles};

    ${({ $subCategory }) =>
        $subCategory &&
        css`
            @media (max-width: 599px) {
                display: none;
            }
        `};

    @media (max-width: 390px) {
        display: none;
    }
`;

const Category = styled(CLink, {
    shouldForwardProp: (propName: string) => !propName.startsWith('$'),
})<SubProp>`
    ${commonLinkStyles};

    ${({ $subCategory }) =>
        $subCategory &&
        css`
            @media (max-width: 430px) {
                display: none;
            }
        `};
`;

export const CommonHero = ({
    children,
    heading,
    btns,
    blogTitle,
}: {
    children?: React.ReactNode;
    heading?: string;
    btns?: React.ReactNode;
    blogTitle?: string;
}) => {
    const { category, subCategory, title } = useLocationData();

    return (
        <Hero>
            <TextBox>
                <BreadCrumb>
                    <Home to="/" $subCategory={subCategory}>
                        Home<span>|</span>
                    </Home>
                    {category && (
                        <Category to={getNavLink(category)} $subCategory={subCategory}>
                            {category}
                            <span>|</span>
                        </Category>
                    )}
                    {subCategory && (
                        <CLink to={getNavLink(subCategory)} css={commonLinkStyles}>
                            {subCategory}
                            <span>|</span>
                        </CLink>
                    )}
                    {blogTitle ? <span>{blogTitle}</span> : title && <span>{title}</span>}
                </BreadCrumb>
                <h1>{blogTitle || heading ? heading : title ? title : category}</h1>
                {btns || ''}
            </TextBox>
            <div>{children}</div>
        </Hero>
    );
};
